/**
 * Direct GCS Upload Utility
 * Uploads files directly to Google Cloud Storage using signed URLs.
 * Bypasses Cloud Run's 32MB limit - supports files up to 200MB+
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Upload a single file directly to GCS
 * @param {File} file - The file to upload
 * @param {string} token - Auth token
 * @param {function} onProgress - Progress callback (0-100)
 * @returns {Promise<object>} - Document info after upload
 */
export async function uploadFileToGCS(file, token, onProgress = () => {}) {
  try {
    // Step 1: Get signed upload URL from backend
    const formData = new FormData();
    formData.append('filename', file.name);
    formData.append('content_type', file.type || 'application/octet-stream');

    const urlResponse = await fetch(`${API_BASE}/api/documents/get-upload-url`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!urlResponse.ok) {
      const error = await urlResponse.json();
      throw new Error(error.detail || 'Failed to get upload URL');
    }

    const { upload_url, blob_path, content_type } = await urlResponse.json();
    
    onProgress(10); // Got signed URL

    // Step 2: Upload directly to GCS
    const xhr = new XMLHttpRequest();
    
    const uploadPromise = new Promise((resolve, reject) => {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          // Progress from 10-90% during actual upload
          const percent = 10 + (e.loaded / e.total) * 80;
          onProgress(Math.round(percent));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`GCS upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error during GCS upload'));
      xhr.ontimeout = () => reject(new Error('Upload timeout'));
    });

    xhr.open('PUT', upload_url);
    xhr.setRequestHeader('Content-Type', content_type);
    xhr.timeout = 600000; // 10 minute timeout for large files
    xhr.send(file);

    await uploadPromise;
    
    onProgress(90); // Upload complete

    // Step 3: Confirm upload with backend
    const confirmData = new FormData();
    confirmData.append('blob_path', blob_path);
    confirmData.append('filename', file.name);
    confirmData.append('file_size', file.size);

    const confirmResponse = await fetch(`${API_BASE}/api/documents/confirm-upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: confirmData,
    });

    if (!confirmResponse.ok) {
      const error = await confirmResponse.json();
      throw new Error(error.detail || 'Failed to confirm upload');
    }

    const result = await confirmResponse.json();
    
    onProgress(100); // All done

    return result.document;
  } catch (error) {
    console.error('GCS upload error:', error);
    throw error;
  }
}

/**
 * Upload multiple files directly to GCS
 * @param {File[]} files - Array of files to upload
 * @param {string} token - Auth token
 * @param {function} onProgress - Progress callback ({ current, total, percent, filename })
 * @returns {Promise<object[]>} - Array of document info
 */
export async function uploadFilesToGCS(files, token, onProgress = () => {}) {
  const results = [];
  const total = files.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    try {
      onProgress({
        current: i + 1,
        total,
        percent: Math.round((i / total) * 100),
        filename: file.name,
        status: 'uploading'
      });

      const doc = await uploadFileToGCS(file, token, (filePercent) => {
        const overallPercent = Math.round(((i + filePercent / 100) / total) * 100);
        onProgress({
          current: i + 1,
          total,
          percent: overallPercent,
          filename: file.name,
          filePercent,
          status: 'uploading'
        });
      });

      results.push({ success: true, document: doc, filename: file.name });

    } catch (error) {
      console.error(`Failed to upload ${file.name}:`, error);
      results.push({ success: false, error: error.message, filename: file.name });
    }
  }

  onProgress({
    current: total,
    total,
    percent: 100,
    status: 'complete'
  });

  return results;
}

export default { uploadFileToGCS, uploadFilesToGCS };
